import React, { useState, useEffect } from 'react';
import * as mui from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import GppBadIcon from '@mui/icons-material/GppBad';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const DashboardOverview = ({ 
  app, 
  company, 
  appId, 
  companyId, 
  nextStep,
  risks,
  controls,
  workpapers,
  goToStep
}) => {
  // Function to handle navigation to different steps
  const handleGoToStep = (step) => {
    if (goToStep) {
      goToStep(step);
    }
  };

  // Prepare chart data for risk ratings
  const riskRatingData = [
    { id: 0, value: risks.high || 0, label: 'High', color: '#d32f2f' },
    { id: 1, value: risks.medium || 0, label: 'Medium', color: '#ff9800' },
    { id: 2, value: risks.low || 0, label: 'Low', color: '#2e7d32' },
    { id: 3, value: risks.unrated || 0, label: 'Unrated', color: '#757575' }
  ];

  // Filter out any entries with zero value to avoid empty pie segments
  const filteredRiskRatingData = riskRatingData.filter(item => item.value > 0);

  // Risk types data
  const riskTypeData = [
    { riskType: 'IT', value: 4 },
    { riskType: 'Business Process', value: 7 },
    { riskType: 'Operational', value: 2 },
    { riskType: 'Entity Level', value: 1 }
  ];

  return (
    <mui.Box>
      <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <mui.Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
          <DashboardIcon sx={{ mr: 1 }} />
          Risk & Control Dashboard for {app?.APP_NAME}
        </mui.Typography>
        
        {/* Start Button - Moved to top */}
        <mui.Button
          variant="contained"
          color="primary"
          onClick={nextStep}
          endIcon={<ArrowForwardIcon />}
          sx={{ fontWeight: 500 }}
        >
          Start Risk Identification
        </mui.Button>
      </mui.Box>
      
      {/* Instructional message */}
      <mui.Paper 
        elevation={1}
        sx={{ 
          p: 2.5, 
          mb: 4, 
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <mui.Box 
          sx={{ 
            mr: 2,
            display: 'flex',
            bgcolor: 'primary.main',
            color: 'white',
            p: 1,
            borderRadius: 1
          }}
        >
          <mui.Icon>info</mui.Icon>
        </mui.Box>
        <mui.Typography variant="body1" color="text.primary" fontWeight={400}>
          Welcome to the Risk & Control Dashboard. To begin the assessment process, click the <strong>"Start Risk Identification"</strong> button to identify relevant risks for your application.
        </mui.Typography>
      </mui.Paper>

      {/* Summary Cards */}
      <mui.Grid container spacing={3} sx={{ mb: 4 }}>
        <mui.Grid item xs={12} sm={6} md={6}>
          <mui.Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <WarningIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
            <mui.Typography variant="h4" fontWeight="bold">
              {risks.total || 0}
            </mui.Typography>
            <mui.Typography variant="subtitle1" color="text.secondary">
              Risks Identified
            </mui.Typography>
            <mui.Button
              size="small"
              variant="text"
              endIcon={<ArrowForwardIcon />}
              onClick={() => handleGoToStep(1)}
              sx={{ mt: 2 }}
            >
              Manage Risks
            </mui.Button>
          </mui.Paper>
        </mui.Grid>

        <mui.Grid item xs={12} sm={6} md={6}>
          <mui.Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <SecurityIcon sx={{ fontSize: 40, mb: 1, color: '#1565c0' }} />
            <mui.Typography variant="h4" fontWeight="bold">
              {controls.mapped || 0}
            </mui.Typography>
            <mui.Typography variant="subtitle1" color="text.secondary">
              Controls Mapped
            </mui.Typography>
            <mui.Button
              size="small"
              variant="text"
              endIcon={<ArrowForwardIcon />}
              onClick={() => handleGoToStep(3)}
              sx={{ mt: 2 }}
            >
              Map Controls
            </mui.Button>
          </mui.Paper>
        </mui.Grid>
      </mui.Grid>

      {/* Risk Rating Chart and Risk Type Breakdown */}
      <mui.Grid container spacing={3} sx={{ mb: 4 }}>
        <mui.Grid item xs={12} md={6}>
          <mui.Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '100%',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <mui.Typography variant="h6" gutterBottom>
              Risk Rating Distribution
            </mui.Typography>
            
            {filteredRiskRatingData.length > 0 ? (
              <PieChart
                series={[
                  {
                    data: filteredRiskRatingData,
                    innerRadius: 60,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    startAngle: -90,
                    endAngle: 270,
                    cx: 150,
                    cy: 150,
                    arcLabel: (item) => `${item.value}`,
                    arcLabelMinAngle: 15,
                  }
                ]}
                width={350}
                height={300}
                slotProps={{
                  legend: {
                    direction: 'column',
                    position: { vertical: 'middle', horizontal: 'right' },
                    padding: 0,
                    itemMarkWidth: 24,
                    itemMarkHeight: 16,
                    markGap: 8,
                    itemGap: 12,
                    labelStyle: {
                      fontSize: 14,
                    },
                  }
                }}
              />
            ) : (
              <mui.Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <mui.Typography variant="body1" color="text.secondary">
                  No risks have been rated yet
                </mui.Typography>
              </mui.Box>
            )}
          </mui.Paper>
        </mui.Grid>
        
        <mui.Grid item xs={12} md={6}>
          <mui.Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              height: '100%',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <mui.Typography variant="h6" gutterBottom>
              Risk Status
            </mui.Typography>
            
            <mui.Box>
              <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <mui.Tooltip title="View all risks with mapped controls" arrow placement="top">
                  <mui.Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: 3,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      width: 120,
                      height: 120,
                      cursor: 'pointer',
                      transition: '0.3s',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        '& .MuiTypography-root': {
                          color: 'white'
                        }
                      },
                      position: 'relative'
                    }}
                    onClick={() => handleGoToStep(3)}
                  >
                    <mui.Typography variant="h4" fontWeight="bold" color="primary.main" className="MuiTypography-root">
                      {controls.risksWithControls || 0}
                    </mui.Typography>
                    <mui.Typography variant="caption" color="primary.main" className="MuiTypography-root">
                      of {risks.total || 0}
                    </mui.Typography>
                  </mui.Box>
                </mui.Tooltip>
                
                <mui.Box sx={{ flexGrow: 1 }}>
                  <mui.Typography variant="subtitle2" gutterBottom>
                    Risks with Mapped Controls
                  </mui.Typography>
                  <mui.LinearProgress 
                    variant="determinate" 
                    value={risks.total > 0 && controls.risksWithControls > 0 ? 
                      (controls.risksWithControls / risks.total) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <mui.Typography variant="body2" color="text.secondary">
                    {risks.total > 0 
                      ? controls.risksWithControls > 0 
                        ? `${Math.round((controls.risksWithControls / risks.total) * 100)}% of risks have controls mapped`
                        : 'No risks have controls mapped yet'
                      : 'No risks identified yet'}
                  </mui.Typography>
                </mui.Box>
              </mui.Box>
              
              <mui.Divider sx={{ my: 2 }} />
              
              <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <mui.Box sx={{ textAlign: 'center' }}>
                  <mui.Tooltip title="View mitigated risks" arrow placement="top">
                    <mui.Button
                      onClick={() => handleGoToStep(3)}
                      sx={{
                        minWidth: 'auto',
                        p: 0,
                        '&:hover': { bgcolor: 'transparent' }
                      }}
                      color="success"
                    >
                      <mui.Typography 
                        variant="h6" 
                        color="success.main"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'success.dark' }
                        }}
                      >
                        {controls.risksWithControls || 0}
                      </mui.Typography>
                    </mui.Button>
                  </mui.Tooltip>
                  <mui.Typography variant="body2" color="text.secondary">
                    Mitigated
                  </mui.Typography>
                </mui.Box>
                
                <mui.Box sx={{ textAlign: 'center' }}>
                  <mui.Tooltip title="View risks needing controls" arrow placement="top">
                    <mui.Button
                      onClick={() => handleGoToStep(3)}
                      sx={{
                        minWidth: 'auto',
                        p: 0,
                        '&:hover': { bgcolor: 'transparent' }
                      }}
                      color="warning"
                    >
                      <mui.Typography 
                        variant="h6" 
                        color="warning.main"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'warning.dark' }
                        }}
                      >
                        {controls.risksNeedingAction || 0}
                      </mui.Typography>
                    </mui.Button>
                  </mui.Tooltip>
                  <mui.Typography variant="body2" color="text.secondary">
                    Needs Action
                  </mui.Typography>
                </mui.Box>
                
                <mui.Box sx={{ textAlign: 'center' }}>
                  <mui.Tooltip title="View unrated risks" arrow placement="top">
                    <mui.Button
                      onClick={() => handleGoToStep(2)}
                      sx={{
                        minWidth: 'auto',
                        p: 0,
                        '&:hover': { bgcolor: 'transparent' }
                      }}
                      color="error"
                    >
                      <mui.Typography 
                        variant="h6" 
                        color="error.main"
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'error.dark' }
                        }}
                      >
                        {risks.unrated || 0}
                      </mui.Typography>
                    </mui.Button>
                  </mui.Tooltip>
                  <mui.Typography variant="body2" color="text.secondary">
                    Unrated Risks
                  </mui.Typography>
                </mui.Box>
              </mui.Box>
            </mui.Box>
          </mui.Paper>
        </mui.Grid>
      </mui.Grid>
    </mui.Box>
  );
};

export default DashboardOverview; 